/**
 * NPCCreator - UI for designing custom LPC NPCs.
 *
 * Combines preset selection, appearance editing (via CharacterCreator's patterns),
 * equipment picking, and combat config into a single panel.
 * Accessible from the editor NPC tool and as a standalone window.
 */

import LPCCompositor from '../character/LPCCompositor.js';
import {
  BODY_TYPE_LIST, CREATOR_CATEGORIES, SKIN_SWATCHES, HAIR_SWATCHES, EYE_SWATCHES,
  VARIANT_SWATCHES, buildLayerStack, getItemsForCategory, getVariantsForItem,
  getSkinVariants, isItemCompatibleWithBody, loadManifest, findItemById, getFileIndex
} from '../character/LPCLayerDefinitions.js';
import { generateRandomAppearance } from '../character/LPCRandomAppearance.js';
import { getPresetIds, getPreset, resolvePreset } from '../npc/LPCEnemyPresets.js';
import { ITEM_DEFS } from '../loot/ItemDefinitions.js';

// Body type → body item overrides
const BODY_ITEM_OVERRIDES = {
  'skeleton-male': { item: 'body_skeleton', variant: 'skeleton' },
  'zombie-male':   { item: 'body_zombie',   variant: 'zombie' }
};

// Equipment slots available for NPC gear
const EQUIPMENT_SLOTS = [
  { id: 'mainhand', label: 'Main Hand' },
  { id: 'offhand',  label: 'Off Hand' },
  { id: 'head',     label: 'Head' },
  { id: 'body',     label: 'Body' },
  { id: 'shoulders', label: 'Shoulders' },
  { id: 'legs',     label: 'Legs' },
  { id: 'feet',     label: 'Feet' },
  { id: 'back',     label: 'Back' }
];

// Appearance tabs
const TABS = [
  { id: 'presets',   label: 'Presets',    icon: '&#9733;' },
  { id: 'body',      label: 'Body',       icon: '&#9899;' },
  { id: 'face',      label: 'Face',       icon: '&#9786;' },
  { id: 'hair',      label: 'Hair',       icon: '&#10047;' },
  { id: 'extras',    label: 'Extras',     icon: '&#10070;' },
  { id: 'equipment', label: 'Equipment',  icon: '&#9876;' }
];

const TAB_SECTIONS = {
  body: ['body'],
  face: ['head', 'face', 'eyes', 'eyebrows'],
  hair: ['hair', 'hairext', 'beard'],
  extras: ['ears', 'nose', 'facial', 'horns', 'tail', 'wings']
};

// Per-category crop regions for thumbnail close-ups.
const HEAD_CROP = { sx: 10, sy: 4, sw: 44, sh: 44 };
const EYES_CROP = { sx: 14, sy: 10, sw: 36, sh: 36 };
const THUMB_CROPS = {
  head: HEAD_CROP, face: HEAD_CROP, eyes: EYES_CROP,
  eyebrows: EYES_CROP, hair: { sx: 6, sy: 0, sw: 52, sh: 44 },
  hairext: { sx: 6, sy: 0, sw: 52, sh: 44 },
  beard: { sx: 10, sy: 12, sw: 44, sh: 44 }, ears: HEAD_CROP,
  nose: EYES_CROP, facial: HEAD_CROP, horns: { sx: 6, sy: 0, sw: 52, sh: 52 },
  tail: null, wings: null
};

// LPC direction row offsets
const DIR_SOUTH = 128;
const DIR_NORTH = 0;
// Body base zPos — layers below this are behind the body
const BODY_ZPOS = 10;

// ── Thumbnail Renderer ───────────────────────────────────────────────
class ThumbnailRenderer {
  constructor() {
    this._bases = {};
    this._cache = new Map();
  }

  async renderBase(appearance, dir = DIR_SOUTH) {
    const bodyEntry = BODY_TYPE_LIST.find(b => b.id === appearance.bodyType);
    const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';
    const skinColor = (appearance.skinColor || 'light').replace(/ /g, '_');
    const headId = appearance.head || 'heads_human_male';
    const newKey = `${bodyKey}:${skinColor}:${headId}`;
    const cached = this._bases[dir];
    if (cached && cached.key === newKey) return cached.canvas;
    const bodyCanvas = document.createElement('canvas');
    bodyCanvas.width = 64; bodyCanvas.height = 64;
    const bodyCtx = bodyCanvas.getContext('2d');
    bodyCtx.imageSmoothingEnabled = false;
    const bodyOverride = BODY_ITEM_OVERRIDES[appearance.bodyType];
    const bodyItemId = bodyOverride ? bodyOverride.item : 'body';
    const bodyVariant = bodyOverride ? bodyOverride.variant : skinColor;
    await this._drawItemFrame(bodyCtx, bodyItemId, bodyKey, bodyVariant, dir);
    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bodyCanvas, 0, 0);
    const headItem = findItemById(headId);
    if (headItem) {
      const headVariant = headItem.matchBodyColor ? skinColor : (headItem.variants?.[0] || skinColor);
      await this._drawItemFrame(ctx, headId, bodyKey, headVariant, dir);
    }
    this._bases[dir] = { canvas, key: newKey, bodyCanvas };
    return canvas;
  }

  getBodyOnlyCanvas(dir = DIR_SOUTH) {
    return this._bases[dir]?.bodyCanvas || null;
  }

  async renderThumbnail(itemId, variant, bodyKey, dir = DIR_SOUTH, minZPos = -Infinity) {
    const baseKey = this._bases[dir]?.key || '';
    const cacheKey = `${itemId}:${variant}:${dir}:${minZPos}:${baseKey}`;
    if (this._cache.has(cacheKey)) return this._cache.get(cacheKey);
    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    const base = this._bases[dir]?.canvas;
    if (base) ctx.drawImage(base, 0, 0);
    if (itemId) await this._drawItemFrame(ctx, itemId, bodyKey, variant, dir, minZPos);
    this._cache.set(cacheKey, canvas);
    return canvas;
  }

  async _drawItemFrame(ctx, itemId, bodyKey, variant, dir = DIR_SOUTH, minZPos = -Infinity) {
    const item = findItemById(itemId);
    if (!item) return;
    const safeVariant = String(variant).replace(/ /g, '_');
    const fileIndex = getFileIndex();
    const sorted = [...item.layers].sort((a, b) => a.zPos - b.zPos);

    // Raw manifest layers use `customAnimation` (not processed `animations`).
    // Attack-only: slash*, thrust*, shoot*, spellcast*, backslash*, halfslash*
    // Idle/walk oversized: walk_128
    // No customAnimation: unrestricted standard 64px layers
    const isAttackOnly = (ca) => ca && !ca.startsWith('walk');
    // If item has walk_128 layers, skip unrestricted 64px dupes
    const hasWalk128 = sorted.some(l => l.customAnimation && l.customAnimation.startsWith('walk'));
    // For thumbnails: only render foreground layers (zPos >= BODY_ZPOS).
    // Back-of-body layers cause visual duplication since thumbnails lack body occlusion.
    const fgOnly = minZPos < BODY_ZPOS ? BODY_ZPOS : minZPos;

    const dirRow = (dir / 64);  // DIR_SOUTH=128 → row 2, DIR_NORTH=0 → row 0
    for (const layer of sorted) {
      if (layer.zPos < fgOnly) continue;
      // Skip attack-only layers (slash, thrust, etc.) — only show idle/walk pose
      if (isAttackOnly(layer.customAnimation)) continue;
      // Skip unrestricted 64px layers when dedicated walk_128 layers exist
      if (hasWalk128 && !layer.customAnimation) continue;
      let path = layer.bodyTypes[bodyKey];
      if (!path) {
        const keys = Object.keys(layer.bodyTypes);
        if (keys.length > 0) path = layer.bodyTypes[keys[0]];
      }
      if (!path || path.indexOf('${') >= 0) continue;
      path = path.replace(/\/$/, '');
      const fi = fileIndex ? fileIndex[path] : null;
      const res = LPCCompositor._resolvePathFromIndex(fi, path, safeVariant, 'idle', 'idle', layer)
        || LPCCompositor._resolvePathFromIndex(fi, path, safeVariant, 'walk', 'walk', layer);
      if (!res) continue;
      try {
        const img = await LPCCompositor._loadImage(`/lpc/spritesheets/${res.path}`);
        const fh = Math.round(img.height / 4);
        const sy = dirRow * fh;
        if (fh <= 64) {
          ctx.drawImage(img, 0, sy, 64, 64, 0, 0, 64, 64);
        } else {
          // Oversized frame — crop center 64px to align with body base
          const offset = Math.floor((fh - 64) / 2);
          ctx.drawImage(img, offset, sy + offset, 64, 64, 0, 0, 64, 64);
        }
      } catch (e) { /* skip */ }
    }
  }

  invalidate() { this._cache.clear(); this._bases = {}; }
  destroy() { this._cache.clear(); this._bases = {}; }
}

const DEFAULT_APPEARANCE = {
  bodyType: 'human-male',
  skinColor: 'light',
  head: 'heads_human_male',
  face: null,
  eyes: null,
  eyeColor: 'blue',
  eyebrows: null,
  eyebrowColor: 'dark brown',
  hair: 'hair_bangs',
  hairColor: 'dark brown',
  hairext: null,
  beard: null,
  beardColor: 'dark brown',
  ears: null,
  nose: null,
  facial: null,
  facialVariant: null,
  horns: null,
  tail: null,
  wings: null
};

export default class NPCCreator {
  constructor(options = {}) {
    this.onConfirm = options.onConfirm || (() => {});
    this.onCancel = options.onCancel || (() => {});

    this.appearance = { ...DEFAULT_APPEARANCE };
    this.equipment = {};  // slot → itemId
    this.npcConfig = {
      displayName: '',
      isHostile: true,
      level: 1,
      aggroRange: 8,
      wanderRadius: 3,
      behaviorType: 'wander'
    };

    this._activeTab = 'presets';
    this._previewCanvas = null;
    this._previewCtx = null;
    this._previewAnimFrame = 0;
    this._previewAnimTimer = null;
    this._previewAtlas = null;
    this._previewImg = null;
    this._previewDirection = 'south';
    this._previewAnim = 'walking-8-frames';

    this._selectedPreset = null;  // currently selected preset id (null = full random)

    this._dragging = false;
    this._dragStartX = 0;

    this._thumbRenderer = new ThumbnailRenderer();
    this._thumbObserver = null;

    this.element = null;
    this._tabContent = null;
  }

  buildContent() {
    const el = document.createElement('div');
    el.className = 'cc-container npc-creator';

    el.innerHTML = `
      <div class="cc-preview-section">
        <canvas class="cc-preview-canvas" width="192" height="192"></canvas>
        <div class="cc-actions">
          <button class="cc-btn cc-btn-cancel">Cancel</button>
          <button class="cc-btn cc-btn-confirm">Confirm Selection</button>
          <button class="cc-btn npc-randomize-btn">Randomize</button>
          <div class="cc-anim-toggle">
            <button class="cc-anim-btn cc-anim-active" data-anim="walking-8-frames">Walk</button>
            <button class="cc-anim-btn" data-anim="breathing-idle">Idle</button>
            <button class="cc-anim-btn" data-anim="weapon-slash">Attack</button>
          </div>
        </div>
      </div>
      <div class="cc-tabs"></div>
      <div class="cc-options-scroll">
        <div class="cc-tab-content"></div>
      </div>
      <div class="cc-credits">
        <div class="cc-credits-authors"></div>
        <div class="cc-credits-lpc">
          <a href="https://liberatedpixelcup.github.io/Universal-LPC-Spritesheet-Character-Generator/"
             target="_blank" rel="noopener">Liberated Pixel Cup Character Generator</a>
        </div>
      </div>
    `;

    this.element = el;
    this._tabContent = el.querySelector('.cc-tab-content');
    this._creditsAuthors = el.querySelector('.cc-credits-authors');

    this._previewCanvas = el.querySelector('.cc-preview-canvas');
    this._previewCtx = this._previewCanvas.getContext('2d');
    this._previewCtx.imageSmoothingEnabled = false;

    this._buildTabs(el.querySelector('.cc-tabs'));

    el.querySelector('.cc-btn-confirm').addEventListener('click', () => this._confirm());
    el.querySelector('.cc-btn-cancel').addEventListener('click', () => this._cancel());
    el.querySelector('.npc-randomize-btn').addEventListener('click', () => this._randomize());

    for (const btn of el.querySelectorAll('.cc-anim-btn')) {
      btn.addEventListener('click', () => {
        el.querySelector('.cc-anim-btn.cc-anim-active')?.classList.remove('cc-anim-active');
        btn.classList.add('cc-anim-active');
        // For the Attack button, resolve anim from equipped weapon instead of hardcoded value
        if (btn.dataset.anim === 'weapon-slash') {
          const anims = this._getEquippedAttackAnims();
          this._previewAnim = anims[0];
        } else {
          this._previewAnim = btn.dataset.anim;
        }
        this._previewAnimFrame = 0;
      });
    }

    this._initPreviewDrag();

    loadManifest().then(() => {
      this._renderTab(this._activeTab);
      this._updatePreview();
    });

    return el;
  }

  // ── Tabs ───────────────────────────────────────────────────────────

  _buildTabs(container) {
    for (const tab of TABS) {
      const btn = document.createElement('button');
      btn.className = 'cc-tab-btn' + (tab.id === this._activeTab ? ' cc-tab-active' : '');
      btn.innerHTML = `<span class="cc-tab-icon">${tab.icon}</span><span class="cc-tab-label">${tab.label}</span>`;
      btn.dataset.tab = tab.id;
      btn.addEventListener('click', () => {
        this._activeTab = tab.id;
        container.querySelectorAll('.cc-tab-btn').forEach(b => b.classList.remove('cc-tab-active'));
        btn.classList.add('cc-tab-active');
        this._renderTab(tab.id);
      });
      container.appendChild(btn);
    }
  }

  // ── Tab Rendering ──────────────────────────────────────────────────

  _renderTab(tabId) {
    if (!this._tabContent) return;
    this._tabContent.innerHTML = '';

    // Disconnect previous thumbnail observers
    if (this._thumbObserver) {
      this._thumbObserver.disconnect();
      this._thumbObserver = null;
    }

    // Pre-render base thumbnails for appearance and equipment tabs
    if (tabId === 'body' || tabId === 'face' || tabId === 'hair' || tabId === 'extras' || tabId === 'equipment') {
      this._thumbRenderer.renderBase(this.appearance, DIR_SOUTH);
    }
    if (tabId === 'extras') {
      this._thumbRenderer.renderBase(this.appearance, DIR_NORTH);
    }

    switch (tabId) {
      case 'presets':    this._renderPresetsTab(); break;
      case 'body':       this._renderAppearanceTab('body'); break;
      case 'face':       this._renderAppearanceTab('face'); break;
      case 'hair':       this._renderAppearanceTab('hair'); break;
      case 'extras':     this._renderAppearanceTab('extras'); break;
      case 'equipment':  this._renderEquipmentTab(); break;
    }
  }

  // ── Presets Tab ────────────────────────────────────────────────────

  _renderPresetsTab() {
    const tc = this._tabContent;

    const section = this._createSection('Enemy Presets');
    const row = section.querySelector('.cc-option-row');
    row.classList.add('cc-thumb-grid');

    for (const presetId of getPresetIds()) {
      const preset = getPreset(presetId);
      const selected = this._selectedPreset === presetId;
      const btn = this._createThumbnailBtn(null, preset.displayName, '_preset', selected);
      btn._thumbPresetId = presetId;
      btn.addEventListener('click', () => {
        this._selectedPreset = presetId;
        const seed = (Math.random() * 0x7FFFFFFF) | 0;
        const resolved = resolvePreset(presetId, seed);
        this.appearance = { ...resolved.lpcAppearance };
        this.equipment = { ...resolved.lpcEquipment };
        this.npcConfig.displayName = resolved.displayName;
        this.npcConfig.isHostile = true;
        if (resolved.combatConfig.isBoss) {
          this.npcConfig.level = 5;
        }
        this._thumbRenderer.invalidate();
        this._renderTab(this._activeTab);
        this._updatePreview();
      });
      row.appendChild(btn);
    }
    tc.appendChild(section);

    // Friendly preset
    const friendlySection = this._createSection('Friendly Presets');
    const friendlyRow = friendlySection.querySelector('.cc-option-row');
    friendlyRow.classList.add('cc-thumb-grid');
    const friendlyBtn = this._createThumbnailBtn(null, 'Random Civilian', '_friendly', false);
    friendlyBtn.addEventListener('click', () => {
      this._selectedPreset = null;
      const seed = (Math.random() * 0x7FFFFFFF) | 0;
      this.appearance = generateRandomAppearance({ seed, bodyType: ['human-male', 'human-female'][seed & 1] });
      this.npcConfig.isHostile = false;
      this.npcConfig.displayName = 'Villager';
      this.npcConfig.level = 1;
      // Civilian gear pools — simple clothes, no heavy armor/weapons
      const civilian = {
        body:  ['tunic', 'tabard', 'longsleeve-shirt', 'mage-robe'],
        legs:  [null, 'cloth-leggings', 'combat-shorts'],
        feet:  [null, 'sandals', 'cloth-shoes', 'leather-boots'],
        back:  [null, null, 'tattered-cloak', 'ragged-cloak', 'woodsman-cloak', 'traveler-cloak'],
        head:  [null, null, null, 'cloth-hood']
      };
      this.equipment = {};
      for (const [slot, pool] of Object.entries(civilian)) {
        const pick = pool[(Math.random() * pool.length) | 0];
        if (pick) this.equipment[slot] = pick;
      }
      this._thumbRenderer.invalidate();
      this._renderTab(this._activeTab);
      this._updatePreview();
    });
    friendlyRow.appendChild(friendlyBtn);
    tc.appendChild(friendlySection);

    this._observeThumbnails(section);
    this._observeThumbnails(friendlySection);
  }

  // ── Appearance Tabs (body, face, hair) ─────────────────────────────

  _renderAppearanceTab(tabId) {
    const tc = this._tabContent;
    const sections = TAB_SECTIONS[tabId] || [];

    if (tabId === 'body') {
      // Body type selector
      const bodySection = this._createSection('Body Type');
      const bodyRow = bodySection.querySelector('.cc-option-row');
      bodyRow.classList.add('cc-thumb-grid');
      for (const bt of BODY_TYPE_LIST) {
        const btn = this._createThumbnailBtn(null, bt.label || bt.name, 'body', this.appearance.bodyType === bt.id);
        btn._thumbBodyType = bt;
        btn.addEventListener('click', () => {
          this.appearance.bodyType = bt.id;
          this._autoMatchBodyType(bt.bodyKey);
          this._thumbRenderer.invalidate();
          this._renderTab(tabId);
          this._updatePreview();
        });
        bodyRow.appendChild(btn);
      }
      tc.appendChild(bodySection);
      this._observeThumbnails(bodySection);

      // Skin color
      const skinSection = this._createSection('Skin Color');
      const skinRow = skinSection.querySelector('.cc-option-row');
      for (const variant of getSkinVariants()) {
        const swatch = this._createSwatch(
          SKIN_SWATCHES[variant] || '#888',
          variant,
          this.appearance.skinColor === variant
        );
        swatch.addEventListener('click', () => {
          this.appearance.skinColor = variant;
          this._thumbRenderer.invalidate();
          this._renderTab(tabId);
          this._updatePreview();
        });
        skinRow.appendChild(swatch);
      }
      tc.appendChild(skinSection);
      return;
    }

    // Category sections (face, hair, extras tabs)
    for (const catKey of sections) {
      const cat = CREATOR_CATEGORIES[catKey];
      if (!cat) continue;

      const bodyEntry = BODY_TYPE_LIST.find(b => b.id === this.appearance.bodyType);
      const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';
      const items = getItemsForCategory(catKey, bodyKey);

      const section = this._createSection(cat.label);
      const row = section.querySelector('.cc-option-row');
      row.classList.add('cc-thumb-grid');

      // "None" option
      const currentId = this.appearance[catKey];
      const noneBtn = this._createThumbnailBtn(null, 'None', catKey, !currentId);
      noneBtn.addEventListener('click', () => {
        this.appearance[catKey] = null;
        this._renderTab(tabId);
        this._updatePreview();
      });
      row.appendChild(noneBtn);

      for (const item of items) {
        const label = item.label || item.name || item.id.replace(/_/g, ' ');
        const btn = this._createThumbnailBtn(item, label, catKey, currentId === item.id);
        btn.addEventListener('click', () => {
          this.appearance[catKey] = item.id;
          this._renderTab(tabId);
          this._updatePreview();
        });
        row.appendChild(btn);
      }
      tc.appendChild(section);
      this._observeThumbnails(section);

      // Variant/color picker for selected item
      if (currentId) {
        this._showVariants(tc, catKey, currentId);
      } else if (cat.variantType === 'hair') {
        // Always show hair color even with no item selected
        const colorKey = catKey === 'eyebrows' ? 'eyebrowColor'
          : catKey === 'beard' ? 'beardColor' : 'hairColor';
        this._renderColorSwatches(tc, 'Hair Color', HAIR_SWATCHES, colorKey);
      } else if (cat.variantType === 'eye') {
        this._renderColorSwatches(tc, 'Eye Color', EYE_SWATCHES, 'eyeColor');
      }
    }
  }

  _showVariants(container, catKey, itemId) {
    const cat = CREATOR_CATEGORIES[catKey];
    const variants = getVariantsForItem(itemId);
    if (variants.length <= 1) return;

    let swatches, currentVariant, setVariant, label;

    if (cat.variantType === 'hair') {
      swatches = HAIR_SWATCHES;
      label = 'Color';
      if (catKey === 'hair' || catKey === 'hairext') {
        currentVariant = this.appearance.hairColor;
        setVariant = v => { this.appearance.hairColor = v; };
      } else if (catKey === 'beard') {
        currentVariant = this.appearance.beardColor || this.appearance.hairColor;
        setVariant = v => { this.appearance.beardColor = v; };
      } else if (catKey === 'eyebrows') {
        currentVariant = this.appearance.eyebrowColor || this.appearance.hairColor;
        setVariant = v => { this.appearance.eyebrowColor = v; };
      }
    } else if (cat.variantType === 'eye') {
      swatches = EYE_SWATCHES;
      label = 'Eye Color';
      currentVariant = this.appearance.eyeColor;
      setVariant = v => { this.appearance.eyeColor = v; };
    } else {
      const variantKey = catKey + 'Variant';
      const isSkinLike = variants.some(v => v === 'light' || v === 'amber' || v === 'olive');
      const isHairLike = variants.some(v => v === 'blonde' || v === 'ash' || v === 'sandy');

      if (isSkinLike) {
        swatches = SKIN_SWATCHES;
        label = 'Color';
        currentVariant = this.appearance[variantKey] || this.appearance.skinColor;
        setVariant = v => { this.appearance[variantKey] = v; };
      } else if (isHairLike) {
        swatches = HAIR_SWATCHES;
        label = 'Color';
        currentVariant = this.appearance[variantKey] || this.appearance.hairColor;
        setVariant = v => { this.appearance[variantKey] = v; };
      } else {
        const knownCount = variants.filter(v => VARIANT_SWATCHES[v]).length;
        if (knownCount >= variants.length * 0.5) {
          swatches = VARIANT_SWATCHES;
          label = 'Color';
        } else {
          swatches = null;
          label = 'Variant';
        }
        currentVariant = this.appearance[variantKey] || null;
        setVariant = v => { this.appearance[variantKey] = v; };
      }
    }

    const section = this._createSection(label);
    const row = section.querySelector('.cc-option-row');

    if (swatches) {
      // "Match Skin" reset for skin-like variants
      const variantKey = catKey + 'Variant';
      const isSkinLike = variants.some(v => v === 'light' || v === 'amber' || v === 'olive');
      if (isSkinLike) {
        const isActive = !this.appearance[variantKey];
        const resetBtn = document.createElement('button');
        resetBtn.className = 'cc-option-btn' + (isActive ? ' cc-selected' : '');
        resetBtn.textContent = 'Match Skin';
        resetBtn.addEventListener('click', () => {
          delete this.appearance[variantKey];
          this._thumbRenderer.invalidate();
          this._renderTab(this._activeTab);
          this._updatePreview();
        });
        row.appendChild(resetBtn);
      }
      for (const variant of variants) {
        const hex = swatches[variant];
        if (!hex) continue;
        const swatch = this._createSwatch(hex, variant, variant === currentVariant);
        swatch.addEventListener('click', () => {
          setVariant(variant);
          this._thumbRenderer.invalidate();
          this._renderTab(this._activeTab);
          this._updatePreview();
        });
        row.appendChild(swatch);
      }
    } else {
      for (const variant of variants) {
        const name = variant.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const btn = document.createElement('button');
        btn.className = 'cc-option-btn' + (variant === currentVariant ? ' cc-selected' : '');
        btn.textContent = name;
        btn.addEventListener('click', () => {
          setVariant(variant);
          this._thumbRenderer.invalidate();
          this._renderTab(this._activeTab);
          this._updatePreview();
        });
        row.appendChild(btn);
      }
    }
    container.appendChild(section);
  }

  _renderColorSwatches(container, title, swatches, colorKey) {
    const section = this._createSection(title);
    const row = section.querySelector('.cc-option-row');
    for (const [name, hex] of Object.entries(swatches)) {
      const swatch = this._createSwatch(hex, name, this.appearance[colorKey] === name);
      swatch.addEventListener('click', () => {
        this.appearance[colorKey] = name;
        this._thumbRenderer.invalidate();
        this._renderTab(this._activeTab);
        this._updatePreview();
      });
      row.appendChild(swatch);
    }
    container.appendChild(section);
  }

  // ── Equipment Tab ──────────────────────────────────────────────────

  _renderEquipmentTab() {
    const tc = this._tabContent;

    // Pre-render base for equipment thumbnails
    this._thumbRenderer.renderBase(this.appearance, DIR_SOUTH);

    for (const slot of EQUIPMENT_SLOTS) {
      const section = this._createSection(slot.label);
      const row = section.querySelector('.cc-option-row');
      row.classList.add('cc-thumb-grid');

      const currentItemId = this.equipment[slot.id] || null;

      // "None" button
      const noneBtn = this._createThumbnailBtn(null, 'None', '_equip', !currentItemId);
      noneBtn._thumbEquipSlot = slot.id;
      noneBtn.addEventListener('click', () => {
        delete this.equipment[slot.id];
        this._renderTab('equipment');
        this._updatePreview();
      });
      row.appendChild(noneBtn);

      // Filter ITEM_DEFS to items with lpcLayer that fit this slot
      const candidates = Object.entries(ITEM_DEFS)
        .filter(([, def]) => def.slot === slot.id && def.lpcLayer)
        .sort((a, b) => (a[1].name || a[0]).localeCompare(b[1].name || b[0]));

      for (const [itemId, def] of candidates) {
        const lpc = def.lpcLayer;
        // Normalize: lpcLayer can be a single object or an array of layers
        const lpcArr = Array.isArray(lpc) ? lpc : [lpc];
        const firstLpc = lpcArr[0];
        const lpcItem = firstLpc ? findItemById(firstLpc.layerId) : null;
        const btn = this._createThumbnailBtn(
          lpcItem,
          def.name || itemId,
          '_equip',
          currentItemId === itemId
        );
        // Store equipment-specific data for thumbnail rendering
        btn._thumbEquipVariant = firstLpc ? firstLpc.variant : null;
        btn._thumbEquipLayers = lpcArr;
        btn._thumbEquipSlot = slot.id;
        btn.addEventListener('click', () => {
          this.equipment[slot.id] = itemId;
          this._renderTab('equipment');
          this._updatePreview();
        });
        row.appendChild(btn);
      }
      tc.appendChild(section);
      this._observeThumbnails(section);
    }
  }

  // ── Randomize ──────────────────────────────────────────────────────

  _randomize() {
    this._selectedPreset = null;
    const seed = (Math.random() * 0x7FFFFFFF) | 0;
    this.appearance = generateRandomAppearance({ seed });
    this.npcConfig.displayName = '';
    this.npcConfig.isHostile = true;
    this.npcConfig.level = 1;

    // Randomize equipment — group ITEM_DEFS by slot, pick one per slot (or none)
    this.equipment = {};
    const bySlot = {};
    for (const [id, def] of Object.entries(ITEM_DEFS)) {
      if (!def.slot || !def.lpcLayer) continue;
      (bySlot[def.slot] || (bySlot[def.slot] = [])).push(id);
    }
    for (const [slot, items] of Object.entries(bySlot)) {
      if (Math.random() < 0.4) continue;  // 40% chance to leave slot empty
      this.equipment[slot] = items[(Math.random() * items.length) | 0];
    }

    this._thumbRenderer.invalidate();
    this._renderTab(this._activeTab);
    this._updatePreview();
  }

  // ── Auto-match body type ───────────────────────────────────────────

  _autoMatchBodyType(bodyKey) {
    const bodyType = this.appearance.bodyType;
    if (bodyType && bodyType.startsWith('skeleton')) {
      this.appearance.head = 'heads_skeleton';
    } else if (bodyType && bodyType.startsWith('zombie')) {
      this.appearance.head = 'heads_zombie';
    } else {
      const head = this.appearance.head;
      if (head && (head.startsWith('heads_human') || head === 'heads_skeleton' || head === 'heads_zombie')) {
        const headMap = {
          'male': 'heads_human_male', 'female': 'heads_human_female',
          'muscular': 'heads_human_male', 'teen': 'heads_human_male',
          'child': 'heads_human_child'
        };
        if (headMap[bodyKey]) this.appearance.head = headMap[bodyKey];
      }
    }
    // Clear incompatible items
    const slots = ['hair', 'hairext', 'beard', 'eyes', 'eyebrows', 'ears', 'nose', 'facial', 'horns', 'tail', 'wings', 'face'];
    for (const slot of slots) {
      const itemId = this.appearance[slot];
      if (itemId && !isItemCompatibleWithBody(itemId, bodyKey)) {
        this.appearance[slot] = null;
      }
    }
  }

  // ── Credits ──────────────────────────────────────────────────────

  _updateCredits() {
    if (!this._creditsAuthors) return;

    const SLOT_LABELS = {
      body: 'Body', head: 'Head', face: 'Face', eyes: 'Eyes',
      eyebrows: 'Eyebrows', hair: 'Hair', hairext: 'Hair Ext.',
      beard: 'Beard', ears: 'Ears', nose: 'Nose', facial: 'Facial',
      horns: 'Horns', tail: 'Tail', wings: 'Wings'
    };

    const appearanceSlots = [
      'head', 'face', 'eyes', 'eyebrows', 'hair', 'hairext',
      'beard', 'ears', 'nose', 'facial', 'horns', 'tail', 'wings'
    ];

    const getAuthors = (item) => {
      if (!item || !item.credits) return null;
      const authors = new Set();
      for (const credit of item.credits) {
        for (const author of credit.authors) authors.add(author);
      }
      return authors.size > 0 ? [...authors].sort((a, b) => a.localeCompare(b)) : null;
    };

    const entries = [];

    // Body type credits
    const bodyType = this.appearance.bodyType;
    const bodyItemId = bodyType === 'skeleton-male' ? 'body_skeleton'
      : bodyType === 'zombie-male' ? 'body_zombie' : 'body';
    const bodyAuthors = getAuthors(findItemById(bodyItemId));
    if (bodyAuthors) entries.push({ label: 'Body', authors: bodyAuthors });

    // Appearance slot credits
    for (const key of appearanceSlots) {
      const itemId = this.appearance[key];
      if (!itemId) continue;
      const authors = getAuthors(findItemById(itemId));
      if (authors) entries.push({ label: SLOT_LABELS[key], authors });
    }

    // Equipment slot credits
    for (const slot of EQUIPMENT_SLOTS) {
      const itemId = this.equipment[slot.id];
      if (!itemId) continue;
      const def = ITEM_DEFS[itemId];
      if (!def || !def.lpcLayer) continue;
      const authors = getAuthors(findItemById(def.lpcLayer.layerId));
      if (authors) entries.push({ label: slot.label, authors });
    }

    this._creditsAuthors.innerHTML = '';
    for (const entry of entries) {
      const line = document.createElement('div');
      line.className = 'cc-credit-line';
      line.innerHTML = `<span class="cc-credit-label">${entry.label}:</span> ${entry.authors.join(', ')}`;
      this._creditsAuthors.appendChild(line);
    }
  }

  // ── Preview ────────────────────────────────────────────────────────

  /**
   * Get attack animation names based on equipped mainhand weapon.
   * Falls back to default slash/thrust/shoot when no weapon equipped.
   */
  _getEquippedAttackAnims() {
    const weaponId = this.equipment?.mainhand;
    const def = weaponId && ITEM_DEFS[weaponId];
    if (def?.attackAnims) {
      // Collect unique weapon-specific anims
      return [...new Set(def.attackAnims)];
    }
    // Default: compose all common attack anims so punch/kick buttons work
    return ['weapon-slash', 'weapon-thrust', 'weapon-shoot'];
  }

  async _updatePreview() {
    this._updateCredits();
    const equippedDefs = {};
    for (const [slot, itemId] of Object.entries(this.equipment)) {
      const def = ITEM_DEFS[itemId];
      if (def && def.lpcLayer) equippedDefs[slot] = def;
    }
    const layers = buildLayerStack(this.appearance, equippedDefs);
    if (layers.length === 0) return;

    try {
      const attackAnims = this._getEquippedAttackAnims();
      // If currently showing an attack anim, switch to the new weapon's primary attack
      const nonMovementAnims = ['weapon-slash', 'weapon-slash-reverse', 'weapon-thrust',
        'weapon-backslash', 'weapon-halfslash', 'weapon-shoot'];
      if (nonMovementAnims.includes(this._previewAnim)) {
        this._previewAnim = attackAnims[0];
      }
      // Deduplicate while preserving order
      const animSet = new Set([
        'walking-8-frames', 'breathing-idle', 'running-8-frames',
        ...attackAnims
      ]);
      const atlas = await LPCCompositor.compose(layers, [...animSet]);
      this._previewAtlas = atlas;
      if (!this._previewAnimTimer) this._startPreviewAnimation();
    } catch (e) {
      this._drawPlaceholder();
    }
  }

  _startPreviewAnimation() {
    this._previewAnimFrame = 0;
    const animate = () => {
      this._drawPreviewFrame();
      this._previewAnimFrame = (this._previewAnimFrame + 1) % 9;
      this._previewAnimTimer = setTimeout(() => requestAnimationFrame(animate), 120);
    };
    animate();
  }

  _drawPreviewFrame() {
    const ctx = this._previewCtx;
    const canvas = this._previewCanvas;
    if (!ctx || !this._previewAtlas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const dir = this._previewDirection;
    const anim = this._previewAnim;
    const frameCount = this._previewAtlas.getFrameCount(anim, dir);
    if (frameCount === 0) return;

    const frameData = this._previewAtlas.getAnimationFrame(
      anim, dir, this._previewAnimFrame % frameCount
    );

    if (frameData && this._previewAtlas.url) {
      if (!this._previewImg || this._previewImg.src !== this._previewAtlas.url) {
        this._previewImg = new Image();
        this._previewImg.src = this._previewAtlas.url;
      }

      if (this._previewImg.complete) {
        const ts = frameData.tileSize || 64;
        const offset = (ts - 64) / 2;
        ctx.drawImage(
          this._previewImg,
          frameData.x + offset, frameData.y + offset, 64, 64,
          0, 0, 192, 192
        );
      }
    }
  }

  _drawPlaceholder() {
    const ctx = this._previewCtx;
    if (!ctx) return;
    ctx.clearRect(0, 0, 192, 192);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(32, 32, 128, 128);
    ctx.fillStyle = '#888';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Loading...', 96, 100);
  }

  // ── Drag-to-rotate ─────────────────────────────────────────────────

  _initPreviewDrag() {
    const canvas = this._previewCanvas;
    const DIRECTIONS = ['south', 'south-west', 'west', 'north-west', 'north', 'north-east', 'east', 'south-east'];
    const DRAG_THRESHOLD = 30;

    const onStart = (x) => {
      this._dragging = true;
      this._dragStartX = x;
      this._dragDirIndex = DIRECTIONS.indexOf(this._previewDirection);
    };
    const onMove = (x) => {
      if (!this._dragging) return;
      const delta = x - this._dragStartX;
      const steps = Math.round(delta / DRAG_THRESHOLD);
      if (steps === 0) return;
      const newIndex = ((this._dragDirIndex - steps) % 8 + 8) % 8;
      if (DIRECTIONS[newIndex] !== this._previewDirection) {
        this._previewDirection = DIRECTIONS[newIndex];
      }
    };
    const onEnd = () => { this._dragging = false; };

    canvas.addEventListener('mousedown', (e) => { e.preventDefault(); onStart(e.clientX); });
    canvas.addEventListener('touchstart', (e) => { e.preventDefault(); onStart(e.touches[0].clientX); }, { passive: false });
    window.addEventListener('mousemove', (e) => onMove(e.clientX));
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', (e) => { if (this._dragging) onMove(e.touches[0].clientX); });
    window.addEventListener('touchend', onEnd);
  }

  // ── DOM Helpers ────────────────────────────────────────────────────

  _createSection(title) {
    const section = document.createElement('div');
    section.className = 'cc-section';
    section.innerHTML = `
      <div class="cc-section-title">${title}</div>
      <div class="cc-option-row"></div>
    `;
    return section;
  }

  _createOptionBtn(label, selected) {
    const btn = document.createElement('button');
    btn.className = 'cc-option-btn' + (selected ? ' cc-selected' : '');
    btn.textContent = label;
    return btn;
  }

  _createSwatch(hex, title, selected) {
    const swatch = document.createElement('button');
    swatch.className = 'cc-swatch' + (selected ? ' cc-selected' : '');
    swatch.style.backgroundColor = hex;
    swatch.title = title;
    return swatch;
  }

  // ── Thumbnail Previews ──────────────────────────────────────────────

  _createThumbnailBtn(item, label, catKey, selected) {
    const btn = document.createElement('button');
    btn.className = 'cc-thumb-btn' + (selected ? ' cc-selected' : '');

    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    canvas.className = 'cc-thumb-canvas';
    btn.appendChild(canvas);

    const span = document.createElement('span');
    span.className = 'cc-thumb-label';
    span.textContent = label;
    btn.appendChild(span);

    // Store data for lazy loading
    btn._thumbCanvas = canvas;
    btn._thumbItemId = item ? item.id : null;
    btn._thumbCatKey = catKey;

    return btn;
  }

  _observeThumbnails(section) {
    if (!this._thumbObserver) {
      this._thumbObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this._thumbObserver.unobserve(entry.target);
          this._loadThumbnail(entry.target);
        }
      }, {
        root: this.element.querySelector('.cc-options-scroll'),
        rootMargin: '100px 0px'
      });
    }

    section.querySelectorAll('.cc-thumb-btn').forEach(btn => {
      this._thumbObserver.observe(btn);
    });
  }

  async _loadThumbnail(btn) {
    const canvas = btn._thumbCanvas;
    if (!canvas) return;

    const catKey = btn._thumbCatKey;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, 64, 64);
    ctx.fillStyle = 'rgba(8, 12, 24, 0.6)';
    ctx.fillRect(0, 0, 64, 64);

    try {
      // Body type thumbnails — render each body type with its own base
      if (btn._thumbBodyType) {
        const bt = btn._thumbBodyType;
        const skinColor = (this.appearance.skinColor || 'light').replace(/ /g, '_');
        const bodyOverride = BODY_ITEM_OVERRIDES[bt.id];
        const bodyItemId = bodyOverride ? bodyOverride.item : 'body';
        const bodyVariant = bodyOverride ? bodyOverride.variant : skinColor;
        await this._thumbRenderer._drawItemFrame(ctx, bodyItemId, bt.bodyKey, bodyVariant, DIR_SOUTH);
        const headMap = {
          'skeleton-male': 'heads_skeleton', 'zombie-male': 'heads_zombie',
          'human-child': 'heads_human_child', 'human-female': 'heads_human_female'
        };
        const headId = headMap[bt.id] || 'heads_human_male';
        const headItem = findItemById(headId);
        if (headItem) {
          const headVariant = headItem.matchBodyColor ? skinColor : (headItem.variants?.[0] || skinColor);
          await this._thumbRenderer._drawItemFrame(ctx, headId, bt.bodyKey, headVariant, DIR_SOUTH);
        }
        return;
      }

      // Preset thumbnails — render a representative character for this preset
      if (catKey === '_preset') {
        const presetId = btn._thumbPresetId;
        const resolved = resolvePreset(presetId, 42);  // fixed seed for consistent icon
        const app = resolved.lpcAppearance;
        const equip = resolved.lpcEquipment;

        const bodyEntry = BODY_TYPE_LIST.find(b => b.id === app.bodyType);
        const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';
        const skinColor = (app.skinColor || 'light').replace(/ /g, '_');

        // Render body
        const bodyOverride = BODY_ITEM_OVERRIDES[app.bodyType];
        const bodyItemId = bodyOverride ? bodyOverride.item : 'body';
        const bodyVariant = bodyOverride ? bodyOverride.variant : skinColor;
        await this._thumbRenderer._drawItemFrame(ctx, bodyItemId, bodyKey, bodyVariant, DIR_SOUTH);

        // Render head
        const headId = app.head || 'heads_human_male';
        const headItem = findItemById(headId);
        if (headItem) {
          const headVariant = headItem.matchBodyColor ? skinColor : (headItem.variants?.[0] || skinColor);
          await this._thumbRenderer._drawItemFrame(ctx, headId, bodyKey, headVariant, DIR_SOUTH);
        }

        // Render equipment layers
        for (const [, equipItemId] of Object.entries(equip)) {
          const def = ITEM_DEFS[equipItemId];
          if (!def || !def.lpcLayer) continue;
          const lpcArr = Array.isArray(def.lpcLayer) ? def.lpcLayer : [def.lpcLayer];
          for (const lpcEntry of lpcArr) {
            const variant = lpcEntry.variant || 'default';
            await this._thumbRenderer._drawItemFrame(ctx, lpcEntry.layerId, bodyKey, variant, DIR_SOUTH, -Infinity);
          }
        }
        return;
      }

      // Friendly preset thumbnail — render a civilian with tunic
      if (catKey === '_friendly') {
        const app = generateRandomAppearance({ seed: 7 });
        const bodyEntry = BODY_TYPE_LIST.find(b => b.id === app.bodyType);
        const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';
        const skinColor = (app.skinColor || 'light').replace(/ /g, '_');

        const bodyOverride = BODY_ITEM_OVERRIDES[app.bodyType];
        const bodyItemId = bodyOverride ? bodyOverride.item : 'body';
        const bodyVariant = bodyOverride ? bodyOverride.variant : skinColor;
        await this._thumbRenderer._drawItemFrame(ctx, bodyItemId, bodyKey, bodyVariant, DIR_SOUTH);

        const headId = app.head || 'heads_human_male';
        const headItem = findItemById(headId);
        if (headItem) {
          const headVariant = headItem.matchBodyColor ? skinColor : (headItem.variants?.[0] || skinColor);
          await this._thumbRenderer._drawItemFrame(ctx, headId, bodyKey, headVariant, DIR_SOUTH);
        }

        // Tunic
        const tunicDef = ITEM_DEFS['tunic'];
        if (tunicDef && tunicDef.lpcLayer) {
          const lpcArr = Array.isArray(tunicDef.lpcLayer) ? tunicDef.lpcLayer : [tunicDef.lpcLayer];
          for (const lpcEntry of lpcArr) {
            const variant = lpcEntry.variant || 'default';
            await this._thumbRenderer._drawItemFrame(ctx, lpcEntry.layerId, bodyKey, variant, DIR_SOUTH, -Infinity);
          }
        }
        return;
      }

      // Determine direction and layer filtering per category
      const isBackView = catKey === 'tail' || catKey === 'wings';
      const dir = isBackView ? DIR_NORTH : DIR_SOUTH;
      const minZPos = isBackView ? -Infinity : BODY_ZPOS;

      const itemId = btn._thumbItemId;
      const bodyEntry = BODY_TYPE_LIST.find(b => b.id === this.appearance.bodyType);
      const bodyKey = bodyEntry ? bodyEntry.bodyKey : 'male';

      await this._thumbRenderer.renderBase(this.appearance, dir);

      // Equipment thumbnails — render body base + all equipment layers
      if (catKey === '_equip') {
        const isBack = btn._thumbEquipSlot === 'back';
        const equipDir = isBack ? DIR_NORTH : DIR_SOUTH;
        await this._thumbRenderer.renderBase(this.appearance, equipDir);
        const base = this._thumbRenderer._bases[equipDir]?.canvas;
        if (base) ctx.drawImage(base, 0, 0);
        // Render all layers (capes have 2-3: cape_solid + cape_trim + neck_capetie)
        const layers = btn._thumbEquipLayers;
        if (layers && layers.length > 0) {
          for (const lpcEntry of layers) {
            const layerItemId = lpcEntry.layerId;
            const variant = lpcEntry.variant || 'default';
            await this._thumbRenderer._drawItemFrame(ctx, layerItemId, bodyKey, variant, equipDir, -Infinity);
          }
        } else if (itemId) {
          // Fallback: single-layer item
          const variant = btn._thumbEquipVariant || 'default';
          await this._thumbRenderer._drawItemFrame(ctx, itemId, bodyKey, variant, equipDir, -Infinity);
        }
        return;
      }

      const variant = this._getPreviewVariant(catKey, itemId);

      // Head category: use body-only base so candidate head isn't drawn over existing head
      if (catKey === 'head' && itemId) {
        const bodyOnly = this._thumbRenderer.getBodyOnlyCanvas(dir);
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = 64; tmpCanvas.height = 64;
        const tmpCtx = tmpCanvas.getContext('2d');
        tmpCtx.imageSmoothingEnabled = false;
        if (bodyOnly) tmpCtx.drawImage(bodyOnly, 0, 0);
        await this._thumbRenderer._drawItemFrame(tmpCtx, itemId, bodyKey, variant, dir, minZPos);
        const crop = THUMB_CROPS[catKey];
        ctx.drawImage(tmpCanvas, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, 64, 64);
        return;
      }

      const result = await this._thumbRenderer.renderThumbnail(itemId, variant, bodyKey, dir, minZPos);
      if (result) {
        const crop = THUMB_CROPS[catKey];
        if (crop) {
          ctx.drawImage(result, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, 64, 64);
        } else {
          ctx.drawImage(result, 0, 0);
        }
      }
    } catch (e) { /* graceful fallback — canvas stays blank */ }
  }

  _getPreviewVariant(catKey, itemId) {
    const cat = CREATOR_CATEGORIES[catKey];
    if (!cat) return 'default';

    if (cat.variantType === 'hair') {
      if (catKey === 'hair' || catKey === 'hairext') return this.appearance.hairColor || 'dark brown';
      if (catKey === 'beard') return this.appearance.beardColor || this.appearance.hairColor || 'dark brown';
      if (catKey === 'eyebrows') return this.appearance.eyebrowColor || this.appearance.hairColor || 'dark brown';
    }
    if (cat.variantType === 'eye') return this.appearance.eyeColor || 'blue';

    if (!itemId) return this.appearance.skinColor || 'light';

    const item = findItemById(itemId);
    if (item && item.matchBodyColor) return this.appearance.skinColor || 'light';

    const variantKey = catKey + 'Variant';
    if (this.appearance[variantKey]) return this.appearance[variantKey];

    if (item && item.variants && item.variants.length > 0) return item.variants[0];
    return 'default';
  }

  // ── Actions ────────────────────────────────────────────────────────

  _confirm() {
    const cfg = this.npcConfig;
    const result = {
      lpcAppearance: { ...this.appearance },
      lpcEquipment: { ...this.equipment },
      displayName: cfg.displayName || null,
      behaviorType: cfg.behaviorType,
      wanderRadius: cfg.wanderRadius
    };

    if (cfg.isHostile) {
      result.combatConfig = {
        isHostile: true,
        level: cfg.level,
        maxHealth: 30 + cfg.level * 10,
        attackPower: 5 + cfg.level * 2,
        defense: 1 + cfg.level,
        aggroRange: cfg.aggroRange,
        deaggroRange: cfg.aggroRange + 6,
        attackRange: 1.8,
        attackCooldown: 1.0,
        lootTable: cfg.level >= 5 ? 'boss' : cfg.level >= 3 ? 'elite' : 'default'
      };
    }

    this.onConfirm(result);
  }

  _cancel() {
    this.onCancel();
  }

  /**
   * Load a preset into the creator (for editing existing NPC configs).
   * @param {Object} npcConfig - { lpcAppearance, lpcEquipment, displayName, combatConfig, ... }
   */
  loadConfig(npcConfig) {
    if (npcConfig.lpcAppearance) this.appearance = { ...npcConfig.lpcAppearance };
    if (npcConfig.lpcEquipment) this.equipment = { ...npcConfig.lpcEquipment };
    if (npcConfig.displayName) {
      this.npcConfig.displayName = npcConfig.displayName;
    }
    if (npcConfig.combatConfig) {
      this.npcConfig.isHostile = npcConfig.combatConfig.isHostile !== false;
      this.npcConfig.level = npcConfig.combatConfig.level || 1;
      this.npcConfig.aggroRange = npcConfig.combatConfig.aggroRange || 8;
    }
    if (npcConfig.behaviorType) this.npcConfig.behaviorType = npcConfig.behaviorType;
    if (npcConfig.wanderRadius) this.npcConfig.wanderRadius = npcConfig.wanderRadius;
  }

  destroy() {
    if (this._previewAnimTimer) {
      clearTimeout(this._previewAnimTimer);
      this._previewAnimTimer = null;
    }
    this._previewAtlas = null;
    this._previewImg = null;
    this._thumbRenderer?.destroy();
    this._thumbObserver?.disconnect();
    this._thumbObserver = null;
    this.element = null;
    this._tabContent = null;
  }
}
